import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  AcademicCapIcon,
  ArrowRightIcon,
  ArrowsPointingOutIcon,
  CheckIcon,
  ChevronUpDownIcon,
  ItalicIcon,
  LinkIcon,
  SparklesIcon,
  StrikethroughIcon,
  SwatchIcon,
  UnderlineIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { ArrowUpCircleIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import Quill, { Range } from 'quill';
import { Delta, EmitterSource } from 'quill/core';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from '../hooks/usePopper';
import { classNames } from '../utils';
import { extractTextFromQuill } from '../utils/quill';
import Button from './Common/Button';
import Tabs from './Common/Tabs';
import TextField from './Common/TextField';
import Tooltip from './Common/Tooltip';

export const quillColors = [
  'var(--color-primary)',
  '#000000', // Black
  '#FFFFFF', // White
  '#374151', // Gray 600
  '#1f2937', // Gray 900
  '#e60000', // Red
  '#ff9900', // Orange
  '#ffff00', // Yellow
  '#008a00', // Green
  '#0066cc', // Blue
  '#9933ff', // Purple
  '#facccc', // Light Red
  '#ffebcc', // Light Orange
  '#ffffcc', // Light Yellow
  '#cce8cc', // Light Green
  '#cce0f5', // Light Blue
  '#ebd6ff', // Light Purple
  '#bbbbbb', // Light Gray
  '#f06666', // Dark Red
  '#ffc266', // Dark Orange
  '#ffff66', // Dark Yellow
  '#66b966', // Dark Green
  '#66a3e0', // Dark Blue
  '#c285ff', // Dark Purple
  '#888888', // Medium Gray
  '#a10000', // Darker Red
  '#b26b00', // Darker Orange
  '#b2b200', // Darker Yellow
  '#006100', // Darker Green
  '#0047b2', // Darker Blue
  '#6b24b2', // Darker Purple
  '#444444', // Dark Gray
  '#5c0000', // Very Dark Red
  '#663d00', // Very Dark Orange
  '#666600', // Very Dark Yellow
  '#003700', // Very Dark Green
  '#002966', // Very Dark Blue
  '#3d1466', // Very Dark Purple
];

const quilSizes = [
  '4px',
  '6px',
  '8px',
  '10px',
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '24px',
  '28px',
  '32px',
  '36px',
  '40px',
  '48px',
  '56px',
  '64px',
  '72px',
  '80px',
  '96px',
];

const quillFontWeights = [
  { name: 'Thin', value: '100' },
  { name: 'Extra Light', value: '200' },
  { name: 'Light', value: '300' },
  { name: 'Normal', value: '400' },
  { name: 'Medium', value: '500' },
  { name: 'Semi Bold', value: '600' },
  { name: 'Bold', value: '700' },
  { name: 'Extra Bold', value: '800' },
  { name: 'Black', value: '900' },
];

interface CustomToolbarPopupProps {
  quill: Quill | null;
  generateSuggestions?: (
    textSelection: Delta,
    fullLine: Delta,
    prompt: string,
  ) => Promise<any>;
}

const CustomToolbarPopup: React.FC<CustomToolbarPopupProps> = ({
  quill,
  generateSuggestions,
}) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const [showAIToolbar, setShowAIToolbar] = useState<{
    range: Range;
  }>();
  // Store the current active formats (e.g. bold, italic, underline)
  const [activeFormats, setActiveFormats] = useState<{ [key: string]: any }>(
    {},
  );
  const [showLinkSelector, setShowLinkSelector] = useState<{
    range: Range;
    link: string;
  }>();

  // This element will serve as the invisible “anchor” for Popper
  const anchorRef = useRef<HTMLDivElement | null>(null);

  // Initialize Popper with our custom hook. We choose 'top' but add a flip modifier.
  const [setReferenceElement, setPopperElement] = usePopper({
    placement: 'top',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom'],
        },
      },
    ],
  });

  const [setAIToolbarReferenceElement, setAIToolbarPopperElement] = usePopper({
    placement: 'bottom',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top'],
        },
      },
    ],
  });

  useEffect(() => {
    if (!quill) return;

    // Function to update the active formats from the current selection
    const updateFormats = () => {
      const range = quill.getSelection();
      if (range && range.length > 0) {
        const formats = quill.getFormat(range);
        setActiveFormats(formats);
      } else {
        setActiveFormats({});
      }
    };

    const handleTextChange = (
      delta: Delta,
      _: Delta,
      source: EmitterSource,
    ) => {
      updateFormats();

      if (source === 'user') {
        const text = extractTextFromQuill(delta);
        const currentText = quill.getText().replace(/\n$/, '');

        if (text === ' ' && currentText === '  ') {
          quill.blur();
          quill.setText('');
          setShowAIToolbar({ range: { index: 0, length: 0 } });
          setShowToolbar(false);
        }
      }
    };

    const handleSelectionChange = (
      range: Range | null,
      _oldRange: Range | null,
      _source: string,
    ) => {
      if (range && range.length > 0) {
        // Get the bounding rectangle of the current selection.
        const bounds = quill.getBounds(range);
        if (anchorRef.current) {
          anchorRef.current.style.position = 'absolute';
          // Position the anchor at the center of the selection.
          anchorRef.current.style.left = bounds
            ? `${bounds.left + bounds.width / 2}px`
            : '0px';
          anchorRef.current.style.top = `${bounds?.top ?? 0}px`;
          anchorRef.current.style.minHeight = `${bounds?.height ?? 0}px`;
          anchorRef.current.textContent = '\u00A0';
        }
        setShowLinkSelector(undefined);
        setShowAIToolbar(undefined);
        quill.formatText(
          0,
          quill.getLength(),
          'selectionBackground',
          false,
          'api',
        );
        setShowToolbar(true);
        updateFormats();
      } else {
        if (range && !range.length) {
          quill.formatText(
            0,
            quill.getLength(),
            'selectionBackground',
            false,
            'api',
          );
          setShowAIToolbar(undefined);
        }

        setShowToolbar(false);
        setActiveFormats({});
      }
    };

    quill.on('selection-change', handleSelectionChange);
    quill.on('text-change', handleTextChange);

    return () => {
      quill.off('selection-change', handleSelectionChange);
      quill.off('text-change', handleTextChange);
    };
  }, [quill]);

  // Toggle a format and then update the active formats.
  const handleFormat = (format: string) => {
    if (!quill) return;
    const currentFormat = quill.getFormat();
    const value = currentFormat[format] ? false : true;
    quill.format(format, value, 'user');
    // After toggling, update the active formats.
    const range = quill.getSelection();
    if (range && range.length > 0) {
      const formats = quill.getFormat(range);
      setActiveFormats(formats);
    }
  };

  const formatLink = ({ range, link }: { range: Range; link: string }) => {
    if (!quill) return;

    if (range && range.length > 0) {
      if (!link.trim()) {
        quill.format('link', false, 'user');
        setActiveFormats({ ...activeFormats, link: undefined });
      } else {
        quill.format('link', link.trim(), 'user');
        setActiveFormats(quill.getFormat(range));
      }
    }
  };

  const handleTextFormat = (
    color: string | undefined,
    mode: 'text' | 'highlight',
  ) => {
    if (!quill) return;

    const formatKey = mode === 'text' ? 'color' : 'background';
    const range = quill.getSelection();
    if (range && range.length > 0) {
      quill.format(formatKey, color || false, 'user');
      const formats = quill.getFormat(range);
      setActiveFormats(formats);
    }
  };

  const handleSizeFormat = (size: string | undefined) => {
    if (!quill) return;

    const range = quill.getSelection();
    if (range && range.length > 0) {
      quill.format('size', size || false, 'user');
      const formats = quill.getFormat(range);
      setActiveFormats(formats);
    }
  };

  const handleWeightFormat = (fontweight: string | undefined) => {
    if (!quill) return;

    const range = quill.getSelection();
    if (range && range.length > 0) {
      quill.format('fontweight', fontweight || false, 'user');
      const formats = quill.getFormat(range);
      setActiveFormats(formats);
    }
  };

  const handleClearFormat = () => {
    if (!quill) return;

    const range = quill.getSelection();
    if (range && range.length > 0) {
      quill.removeFormat(range.index, range.length, 'user');
      setActiveFormats({});
    }
  };

  return (
    <>
      {/* Invisible element that serves as the Popper reference */}
      <div
        ref={(node) => {
          anchorRef.current = node;
          setReferenceElement(node);
          setAIToolbarReferenceElement(node);
        }}
        className="pointer-events-none"
      />
      {(showToolbar || !!showLinkSelector) &&
        createPortal(
          <div
            ref={setPopperElement}
            className="z-50 flex h-12 justify-stretch space-x-1 rounded border border-gray-200 bg-white p-2 shadow-lg"
          >
            {/* <CustomToolbarButton
              active={activeFormats.bold}
              onClick={() => handleFormat('bold')}
              icon={<BoldIcon className="size-4" />}
              title="Bold"
            /> */}
            <CustomToolbarButton
              active={activeFormats.italic}
              onClick={() => handleFormat('italic')}
              icon={<ItalicIcon className="size-4" />}
              title="Italic"
            />
            <CustomToolbarButton
              active={activeFormats.underline}
              onClick={() => handleFormat('underline')}
              icon={<UnderlineIcon className="size-4" />}
              title="underline"
            />
            <CustomToolbarButton
              active={activeFormats.strike}
              onClick={() => handleFormat('strike')}
              icon={<StrikethroughIcon className="size-4" />}
              title="strike"
            />

            <Divider />

            <CustomToolbarButton
              active={!!showAIToolbar}
              onClick={() => {
                const range = quill?.getSelection();
                if (!range) return;
                quill?.formatText(range, 'selectionBackground', true, 'api');
                quill?.blur();
                setShowAIToolbar({ range });
                setShowToolbar(false);
              }}
              icon={
                <div className="flex items-center gap-2">
                  <SparklesIcon className="size-4" />
                  <span>Ask AI</span>
                </div>
              }
              title="Ask AI"
            />

            <Divider />

            {/* {generateSuggestions ? (
              <>
                <AISuggestionsButton
                  generateSuggestions={generateSuggestions}
                  quill={quill}
                />

                <Divider />
              </>
            ) : (
              <></>
            )} */}

            <TextColorButton
              activeFormats={activeFormats}
              onChange={handleTextFormat}
            />

            <CustomToolbarButton
              active={!!activeFormats.link}
              onClick={() => {
                const range = quill?.getSelection();
                if (!range) return;

                if (activeFormats.link) {
                  setShowLinkSelector(undefined);
                  formatLink({ range, link: '' });
                } else {
                  setShowLinkSelector({
                    range,
                    link: '',
                  });
                }
              }}
              icon={<LinkIcon className="size-4" />}
              title="Link"
            />

            <Divider />

            <FontSizeButton
              activeFormats={activeFormats}
              onChange={handleSizeFormat}
            />

            <FontWeightButton
              activeFormats={activeFormats}
              onChange={handleWeightFormat}
            />

            <Divider />

            {/* <CustomToolbarButton
              active={false}
              onClick={() => handleClearFormat()}
              icon={<SparklesIcon className="size-4" />}
              title="Rewrite"
            />

            <Divider /> */}

            <CustomToolbarButton
              active={false}
              onClick={() => handleClearFormat()}
              icon={<XCircleIcon className="size-4" />}
              title="Clear Format"
            />

            {!!showLinkSelector && (
              <div className="absolute -inset-1 flex items-center bg-white px-2">
                <TextField
                  fullWidth
                  placeholder="Type or paste a URL, click enter to apply"
                  value={showLinkSelector.link}
                  onChange={(e) => {
                    setShowLinkSelector({
                      ...showLinkSelector,
                      link: e.target.value,
                    });
                  }}
                  input={{
                    onKeyDown: (event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        event.stopPropagation();

                        formatLink({ ...showLinkSelector });
                        setShowLinkSelector(undefined);
                      } else if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        setShowLinkSelector(undefined);
                      }
                    },
                  }}
                />
              </div>
            )}
          </div>,
          document.body,
        )}
      {generateSuggestions &&
        showAIToolbar &&
        createPortal(
          <div
            ref={setAIToolbarPopperElement}
            className="relative z-50 h-12 w-96 max-w-full rounded border border-gray-200 bg-white shadow-lg"
          >
            <CustomAISuggestionsToolbarContent
              onClose={() => setShowAIToolbar(undefined)}
              quill={quill}
              range={showAIToolbar?.range || null}
              generateSuggestions={generateSuggestions}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

export default CustomToolbarPopup;

interface CustomToolbarButtonProps {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  title: string;
}

const CustomToolbarButton = ({
  active,
  onClick,
  icon,
  title,
}: CustomToolbarButtonProps) => {
  return (
    <Tooltip text={title} wrapperClassName="h-full">
      <button
        className={`block h-full rounded px-2 py-1 text-sm font-semibold ${
          active
            ? 'bg-indigo-100 text-indigo-600 hover:bg-gray-100'
            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
        }`}
        onMouseDown={(e) => {
          e.preventDefault();
          onClick();
        }}
      >
        {icon}
      </button>
    </Tooltip>
  );
};

interface CustomToolbarMenuButtonProps {
  button: ({ open }: { open: boolean }) => ReactNode;
  children: ReactNode | (({ close }: { close: () => void }) => ReactNode);
}

const CustomToolbarMenuButton = ({
  button,
  children,
}: CustomToolbarMenuButtonProps) => {
  return (
    <Popover className="h-full" onMouseDown={(e) => e.preventDefault()}>
      {({ open, close }) => (
        <>
          <PopoverButton
            as="div"
            className="h-full"
            onMouseDown={(e) => e.preventDefault()}
          >
            {button({ open })}
          </PopoverButton>
          <PopoverPanel
            onMouseDown={(e) => e.preventDefault()}
            transition
            anchor={{ to: 'bottom', gap: 12, padding: 24 }}
            portal
            className="z-50 h-auto rounded-lg border border-gray-300 bg-white p-1 shadow-sm transition duration-200 ease-in-out data-[closed]:opacity-0"
          >
            {typeof children === 'function' ? children({ close }) : children}
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

const Divider = () => {
  return <hr className="relative h-7 bg-gray-200" style={{ width: 1 }} />;
};

interface TextColorButtonProps {
  activeFormats: { [key: string]: any };
  onChange: (color: string | undefined, mode: 'text' | 'highlight') => void;
}

const TextColorButton = ({ activeFormats, onChange }: TextColorButtonProps) => {
  const [currentTab, setCurrentTab] = useState<'text' | 'highlight'>('text');

  const tabs = useMemo(() => {
    return [
      {
        id: 'text',
        name: 'Text',
        current: false,
      },
      {
        id: 'highlight',
        name: 'Highlight',
        current: false,
      },
    ].map((t) => {
      t.current = t.id === currentTab;
      return t;
    });
  }, [currentTab]);

  const textIcon = <span className="text-md size-4 font-extrabold">A</span>;
  const currentColor =
    activeFormats[currentTab === 'text' ? 'color' : 'background'];

  return (
    <CustomToolbarMenuButton
      button={({ open }) => (
        <CustomToolbarButton
          active={open}
          onClick={() => {}}
          icon={
            <div
              className="-mx-1.5 -my-0.5 rounded-md border border-gray-200 px-1.5 py-0.5"
              style={{
                color: activeFormats.color ?? 'black',
                background: activeFormats.background,
              }}
            >
              {textIcon}
            </div>
          }
          title="Color"
        />
      )}
    >
      {({ close }) => (
        <>
          <Tabs
            size="sm"
            fullWidth
            tabs={tabs}
            onTabClick={(tabId: any) => setCurrentTab(tabId)}
          />
          <div className="flex justify-between py-2">
            <Button
              icon={textIcon}
              variant="secondary"
              className="font-light"
              onClick={() => {
                onChange(undefined, currentTab);
                close();
              }}
            >
              Default
            </Button>

            <Button
              variant="secondary"
              className="relative font-light"
              onClick={(e) => {
                const input = e.currentTarget.parentNode?.querySelector(
                  '#custom-color-picker',
                ) as HTMLInputElement;
                input.click();
              }}
            >
              <input
                id="custom-color-picker"
                type="color"
                className="absolute inset-0 opacity-0"
                value={currentColor}
                onChange={(e) => onChange(e.target.value, currentTab)}
              />
              <SwatchIcon className="size-4" />
            </Button>
          </div>
          <div className="mt-1 grid grid-cols-6 gap-2">
            {quillColors.map((c) => (
              <div
                key={c}
                style={{
                  color: currentTab === 'text' ? c : 'black',
                  backgroundColor: currentTab === 'highlight' ? c : undefined,
                }}
                className={classNames(
                  'aspect-square cursor-pointer rounded-md border-2 bg-gray-50 p-1 text-center leading-none hover:bg-gray-200',
                  c ===
                    activeFormats[
                      currentTab === 'text' ? 'color' : 'background'
                    ]
                    ? 'border-indigo-600'
                    : 'border-transparent hover:border-indigo-300',
                )}
                onClick={() => {
                  onChange(c, currentTab);
                  close();
                }}
              >
                {textIcon}
              </div>
            ))}
          </div>
        </>
      )}
    </CustomToolbarMenuButton>
  );
};

interface FontSizeButtonProps {
  activeFormats: { [key: string]: any };
  onChange: (size: string | undefined) => void;
}

const FontSizeButton = ({ activeFormats, onChange }: FontSizeButtonProps) => {
  return (
    <CustomToolbarMenuButton
      button={({ open }) => (
        <CustomToolbarButton
          active={open}
          onClick={() => {}}
          icon={
            <div className="flex items-center gap-1">
              {activeFormats.size || 'Size'}
              <ChevronUpDownIcon className="size-3" />
            </div>
          }
          title="Text Size"
        />
      )}
    >
      {({ close }) => (
        <>
          <div className="flex w-24 flex-col">
            {activeFormats.size ? (
              <Button
                variant="ternary"
                size="small"
                onClick={() => {
                  onChange(undefined);
                  close();
                }}
              >
                Default
              </Button>
            ) : (
              <></>
            )}
            {quilSizes.map((size) => (
              <Button
                key={size}
                color={activeFormats.size === size ? 'primary' : undefined}
                variant={activeFormats.size === size ? 'secondary' : 'ternary'}
                size="small"
                onClick={() => {
                  onChange(size);
                  close();
                }}
              >
                {size}
              </Button>
            ))}
          </div>
        </>
      )}
    </CustomToolbarMenuButton>
  );
};

interface FontWeightButtonProps {
  activeFormats: { [key: string]: any };
  onChange: (weight: string | undefined) => void;
}

const FontWeightButton = ({
  activeFormats,
  onChange,
}: FontWeightButtonProps) => {
  return (
    <CustomToolbarMenuButton
      button={({ open }) => (
        <CustomToolbarButton
          active={open}
          onClick={() => {}}
          icon={
            <div className="flex items-center gap-1">
              {quillFontWeights.find(
                (w) => w.value === activeFormats.fontweight,
              )?.name || 'Weight'}
              <ChevronUpDownIcon className="size-3" />
            </div>
          }
          title="Weight"
        />
      )}
    >
      {({ close }) => (
        <>
          <div className="flex w-24 flex-col">
            {activeFormats.fontweight ? (
              <Button
                variant="ternary"
                size="small"
                onClick={() => {
                  onChange(undefined);
                  close();
                }}
              >
                Default
              </Button>
            ) : (
              <></>
            )}
            {quillFontWeights.map((fontweight) => (
              <Button
                key={fontweight.value}
                color={
                  activeFormats.fontweight === fontweight.value
                    ? 'primary'
                    : undefined
                }
                variant={
                  activeFormats.fontweight === fontweight.value
                    ? 'secondary'
                    : 'ternary'
                }
                size="small"
                onClick={() => {
                  onChange(fontweight.value);
                  close();
                }}
              >
                {fontweight.name}
              </Button>
            ))}
          </div>
        </>
      )}
    </CustomToolbarMenuButton>
  );
};

interface AISuggestionsButtonPanelProps {
  prompt?: string;
  quill: Quill | null;
  range: Range | null;
  close: () => void;
  generateSuggestions: (
    textSelection: Delta,
    fullLine: Delta,
    prompt: string,
  ) => Promise<any>;
}

const AISuggestionsButtonPanel = ({
  prompt,
  quill,
  range: selectedRange,
  close,
  generateSuggestions,
}: AISuggestionsButtonPanelProps) => {
  const [generating, setGenerating] = useState(false);
  const [range, setRange] = useState<Range | null>(null);
  const [suggestions, setSuggestions] = useState<{ text: string }[]>();
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState(0);

  useEffect(() => {
    if (!quill) return;

    const range = selectedRange || quill.getSelection();
    if (!range) return;

    setRange(range);
    setGenerating(true);
    setSuggestions(undefined);
    setError(null);

    const line = quill.getLine(range.index)[0];

    const lineContent = line
      ? quill.getContents(quill.getIndex(line), line.length())
      : quill.getContents();

    let relevant = true;
    generateSuggestions(
      quill.getContents(range.index, range.length),
      lineContent,
      prompt ?? '',
    )
      .then((res) => {
        if (!relevant) return;

        setSuggestions(res.suggestions);
        setGenerating(false);
      })
      .catch((e) => {
        if (!relevant) return;
        setError('Failed to generate suggestions');
        Sentry.captureException(e);
        setGenerating(false);
      });

    return () => {
      relevant = false;
    };
  }, [generateSuggestions, prompt, quill, selectedRange, requestId]);

  return (
    <div className="flex flex-col gap-1 px-2 py-2">
      {(generating || (!suggestions && !error)) && (
        <>
          {new Array(3)
            .fill(0)
            .map((_, i) => i)
            .map((v) => {
              return (
                <div
                  key={v}
                  className="flex animate-pulse items-center gap-2 py-1"
                >
                  {/* <div className="size-4 rounded bg-gray-200" /> */}
                  <div className="h-4 flex-1 rounded bg-gray-200" />
                </div>
              );
            })}
        </>
      )}
      {!generating && suggestions && (
        <>
          {suggestions.map((suggestion) => {
            return (
              <Button
                key={suggestion.text}
                variant="ternary"
                size="small"
                className="w-full justify-start text-start"
                onClick={() => {
                  if (!quill || !range) return;
                  quill.deleteText(range.index, range.length, 'silent');
                  quill.insertText(range.index, suggestion.text, 'user');

                  close();
                }}
              >
                {suggestion.text}
              </Button>
            );
          })}
        </>
      )}
      {!generating && !suggestions && error && (
        <>
          <div className="flex flex-col items-center gap-2 py-1">
            <span className="text-sm font-semibold text-red-500">{error}</span>
            <Button
              variant="ternary"
              size="small"
              className="w-full justify-start text-start"
              onClick={() => {
                if (!quill || !range) return;
                setRequestId((prev) => prev + 1);
              }}
            >
              Try again
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
interface CustomAISuggestionsToolbarContentProps {
  onClose: () => void;
  quill: Quill | null;
  range: Range | null;
  generateSuggestions: (
    textSelection: Delta,
    fullLine: Delta,
    prompt: string,
  ) => Promise<any>;
}

const CustomAISuggestionsToolbarContent = ({
  onClose,
  quill,
  range,
  generateSuggestions,
}: CustomAISuggestionsToolbarContentProps) => {
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [focusedEditSuggestion, setFocusedEditSuggestion] = useState<
    { id: string; title: string; icon: ReactNode } | undefined
  >();
  const [selectedPrompt, setSelectedPrompt] = useState<string>();

  const editSuggestions = useMemo(() => {
    return range?.length === 0
      ? [
          {
            title: 'Suggested',
            options: [
              {
                id: 'generate',
                title: 'Generate',
                icon: <SparklesIcon className="size-4" />,
              },
            ],
          },
        ]
      : [
          {
            title: 'Suggested',
            options: [
              {
                id: 'improve-writing',
                title: 'Improve writing',
                icon: <SparklesIcon className="size-4" />,
              },
              {
                id: 'fix-spelling-grammar',
                title: 'Fix spelling & grammar',
                icon: <CheckIcon className="size-4" />,
              },
            ],
          },
          {
            title: 'Edit',
            options: [
              {
                id: 'make-longer',
                title: 'Make longer',
                icon: <ArrowsPointingOutIcon className="size-4" />,
              },
              {
                id: 'make-shorter',
                title: 'Make shorter',
                icon: <CheckIcon className="size-4" />,
              },
              {
                id: 'simplify-language',
                title: 'Simplify language',
                icon: <AcademicCapIcon className="size-4" />,
              },
            ],
          },
        ];
  }, [range]);

  const filteredEditSuggestions = useMemo(() => {
    const term = inputValue.toLowerCase().trim();
    return editSuggestions
      .map((group) => ({
        ...group,
        options: group.options.filter((opt) =>
          opt.title.toLowerCase().includes(term),
        ),
      }))
      .filter((group) => group.options.length > 0);
  }, [inputValue, editSuggestions]);

  const handleCreateSuggestions = useCallback(
    (prompt?: string) => {
      if (prompt) {
        setSelectedPrompt(prompt);
      } else {
        const selectedSuggestion = filteredEditSuggestions.find(
          (s) => focusedEditSuggestion?.title === s.title,
        );
        if (!selectedSuggestion) {
          setSelectedPrompt(inputValue);
        } else {
          setSelectedPrompt(selectedSuggestion.title);
        }
      }

      setLoading(true);
    },
    [filteredEditSuggestions, focusedEditSuggestion?.title, inputValue],
  );

  return (
    <div
      className="flex h-full justify-stretch gap-2 pe-2"
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();

          const list = filteredEditSuggestions.flatMap(
            (group) => group.options,
          );
          const currentIndex = list.findIndex(
            (s) => s.id === focusedEditSuggestion?.id,
          );
          const nextIndex =
            currentIndex < list.length - 1 ? currentIndex + 1 : 0;
          setFocusedEditSuggestion(list[nextIndex]);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          e.stopPropagation();
          const list = filteredEditSuggestions.flatMap(
            (group) => group.options,
          );
          const currentIndex = list.findIndex(
            (s) => s.id === focusedEditSuggestion?.id,
          );
          const prevIndex =
            currentIndex > 0 ? currentIndex - 1 : list.length - 1;
          setFocusedEditSuggestion(list[prevIndex]);
        }
      }}
    >
      <div className="absolute -left-3 h-full -translate-x-full">
        <div className="flex h-full items-center">
          <div className="rounded-full bg-white p-1.5 ring-1 ring-gray-300">
            <SparklesIcon className="size-4" />
          </div>
        </div>
      </div>

      <input
        placeholder="Ask AI for suggestions"
        type="text"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            handleCreateSuggestions();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }
        }}
        className={classNames(
          'relative block h-full w-full rounded-s border-0 py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6',
          loading ? 'hidden' : 'block',
        )}
      />

      <div
        className={classNames(
          'h-full w-full items-center px-4',
          loading ? 'flex' : 'hidden',
        )}
      >
        <span>Thinking...</span>
      </div>

      <button
        className="text-indigo-600 disabled:text-gray-300"
        disabled={inputValue.length === 0}
        onClick={() => handleCreateSuggestions(inputValue)}
      >
        <ArrowUpCircleIcon className="size-6" />
      </button>

      {!loading && (
        <div className="absolute top-full mt-2 w-full max-w-64 overflow-scroll rounded border border-gray-200 bg-white pb-1 shadow-lg">
          {filteredEditSuggestions.map((group) => (
            <div key={group.title}>
              <div
                id="group-title"
                className="sticky top-0 z-10 flex h-8 items-center justify-between bg-white px-3 text-xs font-semibold text-gray-500"
              >
                {group.title}
              </div>

              {group.options.map((suggestion) => (
                <div
                  onMouseEnter={() => {
                    setFocusedEditSuggestion(suggestion);
                  }}
                  onMouseLeave={() => {
                    setFocusedEditSuggestion((s) =>
                      s?.id === suggestion.id ? undefined : s,
                    );
                  }}
                  onClick={() => {
                    setFocusedEditSuggestion(suggestion);
                    handleCreateSuggestions(suggestion.title);
                  }}
                  data-focus={
                    focusedEditSuggestion?.id === suggestion.id
                      ? true
                      : undefined
                  }
                  key={suggestion.id}
                  className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-sm text-gray-800 data-[focus]:bg-gray-50 data-[focus]:outline-none"
                >
                  <div className="flex items-center">
                    {suggestion.icon}
                    <span className="ml-3 block truncate font-normal">
                      {suggestion.title}
                    </span>
                  </div>

                  <span className="invisible absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 group-hover:visible group-data-[focus]:visible">
                    <ArrowRightIcon aria-hidden="true" className="size-5" />
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="absolute top-full mt-2 w-full overflow-scroll rounded border border-gray-200 bg-white pb-1 shadow-lg">
          <AISuggestionsButtonPanel
            prompt={selectedPrompt}
            quill={quill}
            range={range}
            close={() => {
              onClose();
            }}
            generateSuggestions={generateSuggestions}
          />
        </div>
      )}
    </div>
  );
};
